import { IPromise } from 'angular';

export const SubscriptionManagementServiceName = 'gpSubscriptionManagementService';

export type SubscriptionProvider = 'stripe' | 'zoho' | 'manual' | 'license' | 'unknown';

export interface LicenseTypeDefinition {
    key: string;
    name: string;
    description?: string;
    defaultSeats?: number;
}

export interface LicenseSummary {
    id: string;
    licenseTypeKey: string;
    licenseTypeName: string;
    licenseTypeDescription?: string;
    ownerTeamId?: string;
    ownerUserId?: string;
    assignedUserId?: string;
    assignedUserDisplayName?: string;
    issuedUtc: string;
    expiresUtc?: string;
    isActive: boolean;
    metadataJson?: string;
}

export interface SubscriptionSummary {
    id: string;
    planCode: string;
    planName: string;
    description?: string;
    provider: SubscriptionProvider;
    status: 'trial' | 'active' | 'past_due' | 'canceled' | 'non_renewing' | 'unknown';
    seatsPurchased: number;
    seatsInUse: number;
    autoRenews: boolean;
    renewsUtc?: string;
    licenseTypeKey?: string;
    linkedTeamId?: string;
    billingEmail?: string;
    metadataJson?: string;
}

export interface PersonalSubscriptionUpsertRequest {
    subscriptionId?: string;
    planCode: string;
    planName?: string;
    seatsPurchased: number;
    licenseTypeKey?: string;
    paymentToken?: string;
    autoRenews?: boolean;
}

export interface TeamSummary {
    id: string;
    name: string;
    description?: string;
    createdUtc?: string;
    createdByUserId?: string;
    members: TeamMemberSummary[];
    subscriptions: SubscriptionSummary[];
    licenses: LicenseSummary[];
    roleOptions: TeamRoleDefinition[];
    pendingInvites: TeamInviteSummary[];
}

export interface TeamInviteSummary {
    email: string;
    invitedByUserId?: string;
    invitedUtc?: string;
    roleKeys: string[];
}

export interface TeamMemberSummary {
    userId: string;
    email: string;
    displayName: string;
    joinedUtc?: string;
    isActive: boolean;
    roleKeys: string[];
    assignedLicenseIds: string[];
}

export interface TeamRoleInfo {
    teamId: string;
    roleKeys: string[];
    canManage: boolean;
}

export interface TeamRoleDefinition {
    key: string;
    name: string;
    description?: string;
}

export interface TeamUpsertRequest {
    name: string;
    description?: string;
}

export interface TeamMemberInviteRequest {
    email: string;
    displayName?: string;
    roleKeys: string[];
}

export interface TeamMemberRoleUpdateRequest {
    roleKeys: string[];
}

export interface LicenseAssignmentRequest {
    licenseId: string;
    userId: string;
    teamId?: string;
}

export interface SubscriptionLinkRequest {
    subscriptionId: string;
    teamId: string;
    seatCount?: number;
}

export interface SeatUpdateRequest {
    subscriptionId: string;
    seatsPurchased: number;
}

export interface ISubscriptionManagementApi {
    getLicenseTypes(): IPromise<LicenseTypeDefinition[]>;

    // Personal subscriptions
    getPersonalSubscriptions(): IPromise<SubscriptionSummary[]>;
    upsertPersonalSubscription(request: PersonalSubscriptionUpsertRequest): IPromise<SubscriptionSummary>;
    cancelPersonalSubscription(subscriptionId: string, reason?: string): IPromise<void>;

    // Teams
    getTeams(): IPromise<TeamSummary[]>;
    createTeam(request: TeamUpsertRequest): IPromise<TeamSummary>;
    updateTeam(teamId: string, request: TeamUpsertRequest): IPromise<TeamSummary>;
    deleteTeam(teamId: string): IPromise<void>;
    getCurrentUserTeamRole(teamId: string): IPromise<TeamRoleInfo>;

    inviteTeamMember(teamId: string, request: TeamMemberInviteRequest): IPromise<TeamSummary>;
    removeTeamMember(teamId: string, userId: string): IPromise<TeamSummary>;
    updateTeamMemberRole(teamId: string, userId: string, request: TeamMemberRoleUpdateRequest): IPromise<TeamSummary>;

    // Licenses
    getLicenses(teamId?: string): IPromise<LicenseSummary[]>;
    assignLicense(request: LicenseAssignmentRequest): IPromise<LicenseSummary>;
    unassignLicense(licenseId: string): IPromise<LicenseSummary>;

    // Team subscriptions and links
    linkSubscriptionToTeam(request: SubscriptionLinkRequest): IPromise<TeamSummary>;
    updateSubscriptionSeats(request: SeatUpdateRequest): IPromise<SubscriptionSummary>;
}
